import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Eye, EyeOff, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const StudentLoginPage = () => {
  const navigate = useNavigate();
  const { loginStudent, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (user && user.role === "student") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginStudent(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      const msg = error.response?.data?.detail || "Invalid credentials";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A] mb-8">
          <Sparkles className="w-5 h-5" />
          <span className="font-medium">Virtually Verbal</span>
        </Link>

        <div className="brutalist-card p-10">
          <h1 className="text-3xl font-bold mb-2">Student Login</h1>
          <p className="text-[#4A4A4A] mb-8">Sign in to continue learning</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="brutalist-input mt-2"
                required
                data-testid="student-email-input"
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="brutalist-input mt-2 pr-12"
                  required
                  data-testid="student-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-[#4A4A4A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Signing in...</> : "Sign In"}
            </button>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#3C8DE4] hover:underline font-semibold" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          <p className="text-center mt-6 text-[#4A4A4A]">
            Don't have an account?{" "}
            <Link to="/register" className="font-bold text-[#0A0A0A] hover:underline">Register</Link>
          </p>

          <div className="mt-6 pt-6 border-t border-[#0A0A0A]/10 text-center">
            <Link to="/tutor/login" className="text-sm text-[#4A4A4A] hover:text-[#0A0A0A]">
              Are you a tutor? Login here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLoginPage;
